import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  Link,
  TextField,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  OpenInNew as OpenInNewIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Download as DownloadIcon,
  InfoOutlined,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import type { AudioPreviewResponse } from './AudioPreview';
import { alpha } from '@mui/material/styles';
import jsPDF from 'jspdf';
import { Canvg } from 'canvg';
import { getWebsiteById } from '../../api';
import { listPronunciationRuleEntries, getPublisherById } from '../../../publishers/api';

// Types
export type Chapter = NonNullable<AudioPreviewResponse['chapters']>[number];

interface ChaptersPanelProps {
  chapters: NonNullable<AudioPreviewResponse['chapters']>;
  onChaptersChange: (chs: NonNullable<AudioPreviewResponse['chapters']>) => void;
  editedLines: Record<number, Array<{ co_host?: string; line?: string }>>;
  onEditedLinesChange: (map: Record<number, Array<{ co_host?: string; line?: string }>>) => void;
  onRegenerate: (chs: NonNullable<AudioPreviewResponse['chapters']>, classification?: AudioPreviewResponse['classification']) => Promise<void> | void;
  disabled?: boolean;
  loading?: boolean;
  onNotify?: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  websiteName?: string;
  websiteId?: string;
  readOnly?: boolean; // New: when true, hide all editing controls
  classification?: {
    incident_types?: string[];
    locations?: string[];
    severity_tags?: string[];
    timestamp?: string;
  };
  publishDate?: string;
  episodeName?: string;
  description?: string;
  issueCastId?: string;
}

const formatTime = (seconds: number): string => {
  const safe = Number.isFinite(seconds) ? seconds : 0;
  const minutes = Math.floor(safe / 60);
  const remainingSeconds = Math.floor(safe % 60);
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

const trimLine = (text: string | undefined, maxLen = 140) => {
  if (!text) return '';
  const oneLine = text.replace(/\s+/g, ' ').trim();
  return oneLine.length > maxLen ? `${oneLine.slice(0, maxLen)}…` : oneLine;
};

const recalcStartTimes = (
  chs: NonNullable<AudioPreviewResponse['chapters']>
): NonNullable<AudioPreviewResponse['chapters']> => {
  let acc = 0;
  return chs.map((c) => {
    const updated = { ...c, startTime: acc };
    const dur = Number.isFinite(c?.runTimeSeconds as number) ? Number(c?.runTimeSeconds) : 0;
    acc += dur;
    return updated;
  });
};

function getChapterCoHostOptions(ch: Chapter): [string, string] {
  const lines = ch?.script?.script ?? [];
  const names = Array.from(new Set((lines.map(l => (l?.co_host || '').trim()).filter(Boolean))));
  if (names.length >= 2) return [names[0], names[1]];
  if (names.length === 1) return [names[0], names[0] === 'Narrator' ? 'Co-Host' : 'Narrator'];
  return ['Narrator A', 'Narrator B'];
}

const ChaptersPanel: React.FC<ChaptersPanelProps> = ({
  chapters,
  onChaptersChange,
  editedLines,
  onEditedLinesChange,
  onRegenerate,
  disabled,
  loading,
  onNotify,
  websiteName,
  websiteId,
  readOnly = false,
  classification,
  publishDate,
  episodeName,
  description,
  issueCastId,
}) => {
  const [expandedChapter, setExpandedChapter] = useState<number | null>(null);
  const [editChapterIndex, setEditChapterIndex] = useState<number | null>(null);
  const [activeLine, setActiveLine] = useState<{ chapterIdx: number; lineIdx: number } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; chapterIndex: number | null }>({ open: false, chapterIndex: null });

  // Dirty tracking state
  const [dirtyLines, setDirtyLines] = useState<Record<number, Set<number>>>({});
  const [dirtyChapters, setDirtyChapters] = useState<Set<number>>(new Set());
  const [dirtyTitles, setDirtyTitles] = useState<Set<number>>(new Set());
  const isLineDirty = (chapterIdx: number, lineIdx: number) => Boolean(dirtyLines[chapterIdx]?.has(lineIdx));
  const isTitleDirty = (chapterIdx: number) => dirtyTitles.has(chapterIdx);
  const isChapterDirty = (chapterIdx: number) => (dirtyChapters.has(chapterIdx) || (dirtyLines[chapterIdx]?.size ?? 0) > 0 || dirtyTitles.has(chapterIdx));
  const markLineDirty = (chapterIdx: number, lineIdx: number) => {
    setDirtyLines((prev) => {
      const next = { ...prev } as Record<number, Set<number>>;
      const set = new Set(next[chapterIdx] ?? []);
      set.add(lineIdx);
      next[chapterIdx] = set;
      return next;
    });
  };
  const markChapterDirty = (chapterIdx: number) => {
    setDirtyChapters((prev) => {
      const next = new Set(prev);
      next.add(chapterIdx);
      return next;
    });
  };
  const markTitleDirty = (chapterIdx: number) => {
    setDirtyTitles((prev) => {
      const next = new Set(prev);
      next.add(chapterIdx);
      return next;
    });
    markChapterDirty(chapterIdx);
  };
  const clearAllDirty = () => {
    setDirtyLines({});
    setDirtyChapters(new Set());
    setDirtyTitles(new Set());
  };

  // Per-line history (undo/redo) state
  type LineValue = { co_host?: string; line?: string };
  const [history, setHistory] = useState<Record<number, Record<number, { undo: LineValue[]; redo: LineValue[] }>>>({});
  const ensureHistory = (chapterIdx: number, lineIdx: number) => {
    setHistory((prev) => {
      const next = { ...prev };
      if (!next[chapterIdx]) next[chapterIdx] = {};
      if (!next[chapterIdx][lineIdx]) next[chapterIdx][lineIdx] = { undo: [], redo: [] };
      return next;
    });
  };
  const getLineCurrent = (chapterIdx: number, lineIdx: number): LineValue => {
    const current = editedLines[chapterIdx] ?? (chapters[chapterIdx]?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
    return { ...(current[lineIdx] || { co_host: '', line: '' }) };
  };
  const pushUndo = (chapterIdx: number, lineIdx: number, prevValue: LineValue) => {
    setHistory((prev) => {
      const next = { ...prev } as typeof prev;
      if (!next[chapterIdx]) next[chapterIdx] = {} as any;
      if (!next[chapterIdx][lineIdx]) next[chapterIdx][lineIdx] = { undo: [], redo: [] };
      const entry = next[chapterIdx][lineIdx];
      const MAX = 100;
      if (entry.undo.length >= MAX) entry.undo.shift();
      entry.undo.push({ ...prevValue });
      entry.redo = [];
      return next;
    });
  };
  const clearHistoryForChapter = (chapterIdx: number) => {
    setHistory((prev) => {
      const next = { ...prev };
      delete next[chapterIdx];
      return next;
    });
  };
  const clearAllHistory = () => setHistory({});

  const handleUndo = () => {
    if (!activeLine) return;
    const { chapterIdx, lineIdx } = activeLine;
    setHistory((prev) => {
      const next = { ...prev };
      const entry = next[chapterIdx]?.[lineIdx];
      if (!entry || entry.undo.length === 0) return prev;
      const previous = entry.undo.pop()!;
      const current = getLineCurrent(chapterIdx, lineIdx);
      entry.redo.push(current);
      // Apply previous value to line
      const currentLines = editedLines[chapterIdx] ?? (chapters[chapterIdx]?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
      const updated = [...currentLines];
      updated[lineIdx] = { ...previous };
      onEditedLinesChange({ ...editedLines, [chapterIdx]: updated });
      syncChapterLines(chapterIdx, updated);
      markLineDirty(chapterIdx, lineIdx);
      return next;
    });
  };

  const handleRedo = () => {
    if (!activeLine) return;
    const { chapterIdx, lineIdx } = activeLine;
    setHistory((prev) => {
      const next = { ...prev };
      const entry = next[chapterIdx]?.[lineIdx];
      if (!entry || entry.redo.length === 0) return prev;
      const nextValue = entry.redo.pop()!;
      const current = getLineCurrent(chapterIdx, lineIdx);
      entry.undo.push(current);
      // Apply next value to line
      const currentLines = editedLines[chapterIdx] ?? (chapters[chapterIdx]?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
      const updated = [...currentLines];
      updated[lineIdx] = { ...nextValue };
      onEditedLinesChange({ ...editedLines, [chapterIdx]: updated });
      syncChapterLines(chapterIdx, updated);
      markLineDirty(chapterIdx, lineIdx);
      return next;
    });
  };

  const canUndo = (() => {
    if (!activeLine) return false;
    const { chapterIdx, lineIdx } = activeLine;
    return Boolean(history[chapterIdx]?.[lineIdx]?.undo.length);
  })();
  const canRedo = (() => {
    if (!activeLine) return false;
    const { chapterIdx, lineIdx } = activeLine;
    return Boolean(history[chapterIdx]?.[lineIdx]?.redo.length);
  })();

  const handleAccordionChange = (idx: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedChapter(isExpanded ? idx : null);
  };

  const handleTitleChange = (chapterIdx: number, newTitle: string) => {
    const updatedChapters = [...chapters];
    updatedChapters[chapterIdx] = {
      ...updatedChapters[chapterIdx],
      title: newTitle,
    };
    onChaptersChange(updatedChapters);
    markTitleDirty(chapterIdx);
  };

  const handleStartEdit = (idx: number) => {
    const ch = chapters[idx];
    const lines = (ch?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
    onEditedLinesChange({ ...editedLines, [idx]: lines });
    setEditChapterIndex(idx);
  };

  const handleDoneEditing = () => {
    setEditChapterIndex(null);
    setActiveLine(null);
  };

  const beginExpandedLineEdit = (chapterIdx: number, lineIdx: number) => {
    setActiveLine({ chapterIdx, lineIdx });
    ensureHistory(chapterIdx, lineIdx);
  };

  // On-change handlers perform immediate save into parent chapters and mark dirty
  const syncChapterLines = (chapterIdx: number, newLines: Array<{ co_host?: string; line?: string }>) => {
    const updatedChapters = chapters.map((c, i) => {
      if (i !== chapterIdx) return c;
      return {
        ...c,
        script: {
          ...(c.script ?? {}),
          script: newLines.map(l => ({ co_host: l.co_host, line: l.line }))
        }
      };
    });
    onChaptersChange(updatedChapters);
  };

  const handleLineTextChange = (chapterIdx: number, lineIdx: number, value: string) => {
    const current = editedLines[chapterIdx] ?? (chapters[chapterIdx]?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
    const prevValue = { ...(current[lineIdx] || { co_host: '', line: '' }) };
    const lines = [...current];
    lines[lineIdx] = { ...lines[lineIdx], line: value };
    pushUndo(chapterIdx, lineIdx, prevValue);
    onEditedLinesChange({ ...editedLines, [chapterIdx]: lines });
    syncChapterLines(chapterIdx, lines);
    markLineDirty(chapterIdx, lineIdx);
  };

  const handleLineHostChange = (chapterIdx: number, lineIdx: number, value: string) => {
    const current = editedLines[chapterIdx] ?? (chapters[chapterIdx]?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
    const prevValue = { ...(current[lineIdx] || { co_host: '', line: '' }) };
    const lines = [...current];
    lines[lineIdx] = { ...lines[lineIdx], co_host: value };
    pushUndo(chapterIdx, lineIdx, prevValue);
    onEditedLinesChange({ ...editedLines, [chapterIdx]: lines });
    syncChapterLines(chapterIdx, lines);
    markLineDirty(chapterIdx, lineIdx);
  };

  const handleDeleteLine = (chapterIdx: number, lineIdx: number) => {
    const current = editedLines[chapterIdx] ?? (chapters[chapterIdx]?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
    const lines = [...current];
    lines.splice(lineIdx, 1);
    onEditedLinesChange({ ...editedLines, [chapterIdx]: lines });
    syncChapterLines(chapterIdx, lines);
    // Mark chapter dirty and clear per-line dirty (indexes shifted)
    markChapterDirty(chapterIdx);
    setDirtyLines((prev) => {
      const next = { ...prev } as Record<number, Set<number>>;
      delete next[chapterIdx];
      return next;
    });
    clearHistoryForChapter(chapterIdx);
  };

  const handleAddLine = (chapterIdx: number, ch: Chapter) => {
    const [hostA, hostB] = getChapterCoHostOptions(ch);
    const current = editedLines[chapterIdx] ?? [];
    const lastHost = (current[current.length - 1]?.co_host || '').trim();
    const nextHost = lastHost === hostA ? hostB : hostA;
    const next = [...current, { co_host: nextHost || hostA, line: '' }];
    onEditedLinesChange({ ...editedLines, [chapterIdx]: next });
    syncChapterLines(chapterIdx, next);
    // Mark the new line and chapter as dirty
    const newIndex = next.length - 1;
    markLineDirty(chapterIdx, newIndex);
    markChapterDirty(chapterIdx);
    clearHistoryForChapter(chapterIdx);
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;
    if (source.index === 0 || destination.index === 0) return;

    const newChapters = [...chapters];
    const [moved] = newChapters.splice(source.index, 1);
    newChapters.splice(destination.index, 0, moved);

    const reorderedWithStarts = recalcStartTimes(newChapters);

    onChaptersChange(reorderedWithStarts);

    setExpandedChapter(null);
    setEditChapterIndex(null);
    onEditedLinesChange({});
    setActiveLine(null);
  };

  const handleDeleteChapter = () => {
    if (deleteConfirmation.chapterIndex === null) return;
    const newChapters = chapters.filter((_, idx) => idx !== deleteConfirmation.chapterIndex);
    const reorderedWithStarts = recalcStartTimes(newChapters);
    onChaptersChange(reorderedWithStarts);
    setDeleteConfirmation({ open: false, chapterIndex: null });
  };

  const handleRegenerateClick = async () => {
    if (disabled || loading) return;
    await onRegenerate(chapters, classification);
    clearAllDirty();
    clearAllHistory();
    onNotify?.('Audio regenerated. Edits are now the new baseline.', 'success');
  };

  const handleDownloadPdf = async () => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      const margin = 56;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const printableWidth = pageWidth - margin * 2;
      let cursorY = margin;

      // Try rendering banner from public folder
      try {
        const base = (typeof process !== 'undefined' && (process as any).env?.PUBLIC_URL) || '';
        const response = await fetch(`${base}/location-station-banner.svg`);
        if (response.ok) {
          const svgText = await response.text();
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const v = await Canvg.fromString(ctx, svgText);
            // Render larger for sharper downscale
            const targetW = Math.min(printableWidth, 520);
            const scale = 2;
            canvas.width = targetW * scale;
            canvas.height = 140 * scale;
            await v.render();
            const imgData = canvas.toDataURL('image/png');
            const aspect = canvas.width > 0 ? canvas.width / canvas.height : 4;
            const imgW = targetW;
            const imgH = imgW / aspect;
            doc.addImage(imgData, 'PNG', margin, cursorY, imgW, imgH, undefined, 'FAST');
            cursorY += imgH + 16;
          }
        }
      } catch {
        // Banner optional
      }

      // Title
      const title = websiteName ? `${websiteName} — Chapters` : 'Chapters';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(title, margin, cursorY);
      cursorY += 24;

      // Podcast Details section (before chapters)
      const addPageIfNeeded = (needed = 0) => {
        if (cursorY + needed > pageHeight - margin) {
          doc.addPage();
          cursorY = margin;
        }
      };

      // Helpers
      const toDataURL = async (url: string): Promise<string | null> => {
        try {
          const res = await fetch(url);
          if (!res.ok) return null;
          const blob = await res.blob();
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
            reader.readAsDataURL(blob);
          });
        } catch {
          return null;
        }
      };
      const measureImage = (dataUrl: string): Promise<{ w: number; h: number }> => new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.width || 1, h: img.height || 1 });
        img.onerror = reject;
        img.src = dataUrl;
      });

      // Compute totals
      const totalRuntimeSec = chapters.reduce((acc, ch) => acc + (Number.isFinite(ch?.runTimeSeconds as number) ? Number(ch?.runTimeSeconds) : 0), 0);
      const totalRuntimeStr = formatTime(totalRuntimeSec);

      // Load website (for sponsor + publisher id) and publisher rules
      let sponsor: { name?: string; imageUrl?: string; clickThroughUrl?: string } | null = null;
      let publisherId: string | null = null;
      if (websiteId) {
        try {
          const site: any = await getWebsiteById(websiteId);
          publisherId = String(site?.publisherPK || site?.publisherId || '');
          const wsSponsor = site?.issueCast?.sponsor;
          if (wsSponsor && (wsSponsor.imageUrl || wsSponsor.name || wsSponsor.clickThroughUrl)) {
            sponsor = { name: wsSponsor.name, imageUrl: wsSponsor.imageUrl, clickThroughUrl: wsSponsor.clickThroughUrl };
          } else if (publisherId) {
            // Best-effort: load publisher to check sponsor at publisher level
            try {
              const pub: any = await getPublisherById(publisherId);
              const pubSponsor = pub?.issueCast?.sponsor;
              if (pubSponsor && (pubSponsor.imageUrl || pubSponsor.name || pubSponsor.clickThroughUrl)) {
                sponsor = { name: pubSponsor.name, imageUrl: pubSponsor.imageUrl, clickThroughUrl: pubSponsor.clickThroughUrl };
              }
            } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      }

      let allRules: any[] = [];
      if (publisherId) {
        try { allRules = await listPronunciationRuleEntries(publisherId); } catch { allRules = []; }
      }

      // Build script text for matching
      const scriptText = chapters
        .flatMap(ch => (ch?.script?.script ?? []).map((ln) => `${ln?.co_host || ''} ${ln?.line || ''}`))
        .join(' \n ');

      // Normalize rules to fields we care about
      const mapRule = (entry: any) => ({
        phrase: entry?.match ?? entry?.search ?? entry?.phrase ?? entry?.text ?? '',
        pronunciation: entry?.displayAs ?? entry?.replace ?? entry?.pronunciation ?? '',
        matchType: entry?.kind ?? entry?.matchType ?? 'exact',
        caseSensitive: !!entry?.caseSensitive,
        enabled: entry?.enabled !== false,
      });

      const usedRules: Array<{ phrase: string; pronunciation: string }> = [];
      if (Array.isArray(allRules) && allRules.length) {
        for (const raw of allRules) {
          const r = mapRule(raw);
          if (!r.enabled || !r.phrase) continue;
          try {
            const hay = r.caseSensitive ? scriptText : scriptText.toLowerCase();
            const needle = r.caseSensitive ? String(r.phrase) : String(r.phrase).toLowerCase();
            let matched = false;
            switch (r.matchType) {
              case 'regex': {
                try {
                  const re = new RegExp(r.phrase, r.caseSensitive ? 'g' : 'gi');
                  matched = re.test(scriptText);
                } catch {
                  matched = false;
                }
                break;
              }
              case 'word': {
                // word boundary match
                const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const re = new RegExp(`\\b${esc}\\b`, 'g');
                matched = re.test(hay);
                break;
              }
              case 'phrase':
              case 'exact':
              default: {
                matched = hay.includes(needle);
                break;
              }
            }
            if (matched) usedRules.push({ phrase: r.phrase, pronunciation: r.pronunciation });
          } catch { /* ignore */ }
        }
      }

      // Render Podcast Details heading
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      addPageIfNeeded(22);
      doc.text('Podcast Details', margin, cursorY);
      cursorY += 22; // increased spacing after main heading

      // Add metadata fields
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);

      if (episodeName) {
        const wrapped = doc.splitTextToSize(`Episode: ${episodeName}`, printableWidth);
        addPageIfNeeded(14 * wrapped.length);
        doc.text(wrapped, margin, cursorY);
        cursorY += 14 * wrapped.length + 4;
      }

      if (description) {
        const wrapped = doc.splitTextToSize(`Description: ${description}`, printableWidth);
        addPageIfNeeded(14 * wrapped.length);
        doc.text(wrapped, margin, cursorY);
        cursorY += 14 * wrapped.length + 4;
      }

      if (publishDate) {
        let dateStr = publishDate;
        try {
          const d = new Date(publishDate);
          if (!isNaN(d.getTime())) {
            dateStr = d.toLocaleString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              timeZoneName: 'short'
            });
          }
        } catch { /* ignore */ }
        const wrapped = doc.splitTextToSize(`Published: ${dateStr}`, printableWidth);
        addPageIfNeeded(14 * wrapped.length);
        doc.text(wrapped, margin, cursorY);
        cursorY += 14 * wrapped.length + 4;
      }

      if (issueCastId) {
        const wrapped = doc.splitTextToSize(`IssueCast ID: ${issueCastId}`, printableWidth);
        addPageIfNeeded(14 * wrapped.length);
        doc.text(wrapped, margin, cursorY);
        cursorY += 14 * wrapped.length + 4;
      }

      // Total runtime
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      addPageIfNeeded(16);
      doc.text(`Total runtime: ${totalRuntimeStr}`, margin, cursorY);
      cursorY += 18; // slightly more space after runtime line

      // Sponsor info
      if (sponsor && (sponsor.name || sponsor.imageUrl || sponsor.clickThroughUrl)) {
        cursorY += 10; // extra space before Sponsor heading
        addPageIfNeeded(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Sponsor', margin, cursorY);
        cursorY += 20; // extra space after Sponsor heading
        doc.setFont('helvetica', 'normal');
        if (sponsor.name) {
          const wrapped = doc.splitTextToSize(`Name: ${sponsor.name}`, printableWidth);
          addPageIfNeeded(14 * wrapped.length);
          doc.text(wrapped, margin, cursorY);
          cursorY += 14 * wrapped.length + 4;
        }
        if (sponsor.clickThroughUrl) {
          const wrapped = doc.splitTextToSize(`URL: ${sponsor.clickThroughUrl}`, printableWidth);
          addPageIfNeeded(14 * wrapped.length);
          doc.text(wrapped, margin, cursorY);
          cursorY += 14 * wrapped.length + 4;
        }
        if (sponsor.imageUrl) {
          try {
            const dataUrl = await toDataURL(sponsor.imageUrl);
            if (dataUrl) {
              const { w, h } = await measureImage(dataUrl);
              const targetH = 40; // px in PDF points
              const scale = targetH / h;
              const imgW = Math.min(printableWidth, Math.max(40, w * scale));
              const imgH = targetH;
              addPageIfNeeded(imgH + 6);
              doc.addImage(dataUrl, 'PNG', margin, cursorY, imgW, imgH, undefined, 'FAST');
              cursorY += imgH + 10; // more space after logo
            }
          } catch { /* ignore logo errors */ }
        }
        cursorY += 12; // space after entire Sponsor block
      }

      // Chapter summary
      cursorY += 10; // extra space before Chapters Summary heading
      addPageIfNeeded(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Chapters Summary', margin, cursorY);
      cursorY += 20; // extra space after heading
      doc.setFont('helvetica', 'normal');
      chapters.forEach((ch, i) => {
        const dur = typeof ch?.runTimeSeconds === 'number' ? formatTime(ch.runTimeSeconds!) : '—';
        const label = `${i + 1}. ${ch?.title || `Chapter ${i + 1}`} — ${dur}`;
        const wrapped = doc.splitTextToSize(label, printableWidth);
        addPageIfNeeded(14 * wrapped.length);
        doc.text(wrapped, margin, cursorY);
        cursorY += 14 * wrapped.length + 4;
      });
      cursorY += 14; // space after summary list

      // Pronunciation rules used
      cursorY += 10; // extra space before Pronunciation Rules Used heading
      addPageIfNeeded(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Pronunciation Rules Used', margin, cursorY);
      cursorY += 20; // extra space after heading
      doc.setFont('helvetica', 'normal');
      if (usedRules.length) {
        usedRules.slice(0, 200).forEach(({ phrase, pronunciation }) => {
          const line = `• ${phrase} — ${pronunciation || '(no pronunciation provided)'}`;
          const wrapped = doc.splitTextToSize(line, printableWidth);
          addPageIfNeeded(14 * wrapped.length);
          doc.text(wrapped, margin, cursorY);
          cursorY += 14 * wrapped.length + 2;
        });
      } else {
        addPageIfNeeded(14);
        doc.text('No pronunciation rules matched this script.', margin, cursorY);
        cursorY += 16;
      }
      const matchedCount = usedRules.length;
      const totalCount = Array.isArray(allRules) ? allRules.length : 0;
      addPageIfNeeded(16);
      doc.setFont('helvetica', 'italic');
      doc.text(`Matched ${matchedCount} of ${totalCount} rules.`, margin, cursorY);
      cursorY += 28; // extra space at bottom of Podcast Details section before chapters (Introduction)

      // Chapters content
      chapters.forEach((ch, cIdx) => {
        const chapterTitle = ch?.title || `Chapter ${cIdx + 1}`;
        // Extra space before chapter heading
        addPageIfNeeded(12 + 28);
        cursorY += 12;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(chapterTitle, margin, cursorY);
        // Extra space after chapter heading
        cursorY += 24;

        const lines = ch?.script?.script ?? [];
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        const hostColW = 110;
        const gap = 8;
        const textW = printableWidth - hostColW - gap;
        lines.forEach((ln) => {
          const hostLabel = `${ln?.co_host || 'Narrator'}:`;
          const text = ln?.line || '';
          const wrapped = doc.splitTextToSize(text, textW);
          const blockHeight = 14 * Math.max(1, wrapped.length);
          addPageIfNeeded(blockHeight);
          // Host column
          doc.setFont('helvetica', 'bold');
          doc.text(hostLabel, margin, cursorY);
          // Line text
          doc.setFont('helvetica', 'normal');
          doc.text(wrapped, margin + hostColW + gap, cursorY);
          cursorY += blockHeight;
        });

        cursorY += 6; // spacing after chapter
      });

      const fname = `podcast-script-${(websiteName || 'website').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
      doc.save(fname);
      onNotify?.('PDF downloaded successfully', 'success');
    } catch (e) {
      onNotify?.('Failed to generate PDF', 'error');
    }
  };

  return (
    <>
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Read-Only Mode
          </Typography>
          <Typography variant="body2">
            The chapters and audio for this IssueCast are locked and cannot be changed. 
            To make edits, click the <strong>Open In Studio</strong> button above to load this content into the Studio editor. 
            From there, you can modify the script and publish a new IssueCast with your changes.
          </Typography>
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1">
            <strong>Chapters</strong>
          </Typography>
          <Link
            href="https://elevenlabs.io/blog/v3-audiotags"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            See ElevenLabs Audio Tags Documentation
            <OpenInNewIcon fontSize="small" />
          </Link>
        </Box>
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPdf}
              title="Download a formatted PDF of the chapters and script"
            >
              Download Chapter PDF
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<UndoIcon />}
              onClick={handleUndo}
              disabled={!canUndo}
              title={!activeLine ? 'Select a line to undo' : (canUndo ? 'Undo last change to this line' : 'Nothing to undo for this line')}
            >
              Undo
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<RedoIcon />}
              onClick={handleRedo}
              disabled={!canRedo}
              title={!activeLine ? 'Select a line to redo' : (canRedo ? 'Redo last undo for this line' : 'Nothing to redo for this line')}
            >
              Redo
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => onNotify?.('Add Article feature is coming soon!', 'info')}
              title="Add an article to this audio"
            >
              Add Article
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<RestoreIcon />}
              disabled={disabled || loading}
              onClick={handleRegenerateClick}
              title="Regenerate audio with your edited script"
            >
              Apply Edits To Audio
            </Button>
          </Box>
        )}
      </Box>

      {classification && (
        <Box sx={{ mt: 1, mb: 2, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1, border: '1px solid rgba(0,0,0,0.05)' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', opacity: 0.8 }}>
            <InfoOutlined fontSize="small" sx={{ mr: 0.5 }} />
            Classification Analysis
          </Typography>
          
          <Grid container spacing={1}>
            {/* Severity Tags */}
            {classification.severity_tags && classification.severity_tags.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ minWidth: 70, color: 'text.secondary', fontWeight: 600 }}>
                    SEVERITY:
                  </Typography>
                  {classification.severity_tags.map((tag: string, i: number) => (
                    <Chip 
                      key={i} 
                      label={tag} 
                      size="small" 
                      color={['life threatening', 'emergency', 'critical', 'warning'].some(k => tag.toLowerCase().includes(k)) ? (tag.toLowerCase().includes('warning') ? 'warning' : 'error') : 'default'} 
                      variant="filled"
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Incident Types */}
            {classification.incident_types && classification.incident_types.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ minWidth: 70, color: 'text.secondary', fontWeight: 600 }}>
                    INCIDENTS:
                  </Typography>
                  {classification.incident_types.map((tag: string, i: number) => (
                    <Chip key={i} label={tag} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Locations */}
            {classification.locations && classification.locations.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ minWidth: 70, color: 'text.secondary', fontWeight: 600 }}>
                    LOCATIONS:
                  </Typography>
                  {classification.locations.map((tag: string, i: number) => (
                    <Chip key={i} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Timestamp */}
            {classification.timestamp && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ minWidth: 70, color: 'text.secondary', fontWeight: 600 }}>
                    GENERATED:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {(() => {
                      try {
                        const date = new Date(classification.timestamp!);
                        if (isNaN(date.getTime())) return classification.timestamp;
                        return date.toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          timeZoneName: 'short'
                        });
                      } catch {
                        return classification.timestamp;
                      }
                    })()}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="chapters-droppable" direction="vertical">
          {(dropProvided) => (
            <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
              {chapters.map((ch, idx) => {
                const firstLine = ch?.script?.script?.[0]?.line || '';
                const preview = trimLine(firstLine);
                const start = typeof ch.startTime === 'number' ? formatTime(ch.startTime) : undefined;
                const dur = typeof ch.runTimeSeconds === 'number' ? formatTime(ch.runTimeSeconds) : undefined;
                const isExpanded = expandedChapter === idx;
                const isEditing = editChapterIndex === idx;
                const [hostA, hostB] = getChapterCoHostOptions(ch!);
                const editableLines = editedLines[idx] ?? (ch?.script?.script ?? []).map(l => ({ co_host: l?.co_host, line: l?.line }));
                const chapterIsDirty = isChapterDirty(idx);
                const titleIsDirty = isTitleDirty(idx);

                return (
                  <Draggable key={`chapter-${idx}`} draggableId={`chapter-${idx}`} index={idx} isDragDisabled={idx === 0 || readOnly}>
                    {(dragProvided, snapshot) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        sx={{ position: 'relative', mb: 1 }}
                        style={{
                          ...(dragProvided.draggableProps.style as React.CSSProperties),
                        }}
                      >
                        {!readOnly && (
                          <Box
                            {...(idx === 0 ? {} : dragProvided.dragHandleProps)}
                            onMouseDown={(e) => { if (idx !== 0) e.stopPropagation(); }}
                            onClick={(e) => { if (idx !== 0) e.stopPropagation(); }}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 16,
                              display: 'flex',
                              alignItems: 'stretch',
                              cursor: idx === 0 ? 'default' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                              backgroundImage: (theme) => {
                                const bars = 'repeating-linear-gradient( to bottom, rgba(0,0,0,0.18), rgba(0,0,0,0.18) 2px, transparent 2px, transparent 6px )';
                                if (!chapterIsDirty) return bars;
                                const tint = `linear-gradient(${alpha(theme.palette.error.light, 0.28)}, ${alpha(theme.palette.error.light, 0.28)})`;
                                return `${bars}, ${tint}`;
                              },
                              zIndex: 2,
                              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                            }}
                            aria-label={idx === 0 ? undefined : 'Drag to reorder chapter'}
                          />
                        )}

                        <Accordion disableGutters sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden', pl: readOnly ? 0 : '20px' }} expanded={isExpanded} onChange={handleAccordionChange(idx)}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    value={ch?.title || ''}
                                    placeholder={`Chapter ${idx + 1}`}
                                    onChange={(e) => handleTitleChange(idx, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      mr: 2,
                                      flexGrow: 1,
                                      '& .MuiInputBase-root': {
                                        backgroundColor: titleIsDirty ? (theme) => alpha(theme.palette.error.light, 0.16) : 'transparent',
                                      },
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    sx={{
                                      pr: 2,
                                      backgroundColor: titleIsDirty ? (theme) => alpha(theme.palette.error.light, 0.16) : 'transparent',
                                      borderRadius: 0.5,
                                      px: titleIsDirty ? 0.5 : 0,
                                    }}
                                  >
                                    {ch?.title || `Chapter ${idx + 1}`}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  {typeof ch?.runTimeSeconds === 'number' && (
                                    <Chip size="small" label={`Runtime ${dur}`} />
                                  )}
                                  {typeof ch?.startTime === 'number' && (
                                    <Chip size="small" variant="outlined" label={`Start ${start}`} />
                                  )}
                                </Box>
                              </Box>
                              {preview && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {preview}
                                </Typography>
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {!readOnly && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                {isEditing ? (
                                  <Stack direction="row" spacing={1}>
                                    <Button size="small" variant="outlined" color="inherit" onClick={handleDoneEditing}>Done</Button>
                                  </Stack>
                                ) : (
                                  <>
                                    <Button
                                      size="small"
                                      startIcon={<DeleteIcon />}
                                      color="error"
                                      onClick={() => setDeleteConfirmation({ open: true, chapterIndex: idx })}
                                    >
                                      Delete
                                    </Button>
                                    <Button
                                      size="small"
                                      startIcon={<EditIcon />}
                                      onClick={() => handleStartEdit(idx)}
                                    >
                                      Edit
                                    </Button>
                                  </>
                                )}
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {ch?.linkToOriginal && (
                                <Typography variant="body2">
                                  Source: <Link href={ch.linkToOriginal} target="_blank" rel="noopener">{ch.linkToOriginal}</Link>
                                </Typography>
                              )}
                              {!isEditing ? (
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                  {Array.isArray(ch?.script?.script) && ch.script!.script!.map((line, li) => (
                                    <li key={li}>
                                      <Box sx={{
                                        borderRadius: 0.5,
                                        px: 1,
                                        py: 0.25,
                                        backgroundColor: isLineDirty(idx, li) ? (theme) => alpha(theme.palette.error.light, 0.16) : 'transparent',
                                      }}>
                                        <Typography variant="body2">
                                          <strong>{line.co_host || 'Narrator'}:</strong> {line.line}
                                        </Typography>
                                      </Box>
                                    </li>
                                  ))}
                                </Box>
                              ) : (
                                <>
                                  <Stack spacing={1}>
                                    {editableLines.map((line, li) => {
                                      const isActive = activeLine?.chapterIdx === idx && activeLine?.lineIdx === li;
                                      const lineIsDirty = isLineDirty(idx, li);
                                      return (
                                        <Box key={li} sx={{ display: 'flex', gap: 1, alignItems: isActive ? 'stretch' : 'center', borderRadius: 1, backgroundColor: lineIsDirty ? (theme) => alpha(theme.palette.error.light, 0.16) : 'transparent', p: lineIsDirty ? 0.75 : 0 }}>
                                          <Select
                                            size="small"
                                            value={(line.co_host || hostA)}
                                            onChange={(e) => handleLineHostChange(idx, li, String(e.target.value))}
                                            sx={{ width: 240, minWidth: 240, '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                                            renderValue={(selected) => (
                                              <Typography noWrap>{String(selected)}</Typography>
                                            )}
                                          >
                                            <MenuItem value={hostA} sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{hostA}</MenuItem>
                                            <MenuItem value={hostB} sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{hostB}</MenuItem>
                                          </Select>

                                          {!isActive ? (
                                            <TextField
                                              size="small"
                                              fullWidth
                                              value={line.line || ''}
                                              onClick={() => beginExpandedLineEdit(idx, li)}
                                              inputProps={{ readOnly: true }}
                                              sx={{ '& .MuiInputBase-input': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' } }}
                                              placeholder="Click to edit"
                                            />
                                          ) : (
                                            <Box sx={{ flex: 1, p: 1, borderRadius: 1, border: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', boxShadow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                              <TextField
                                                autoFocus
                                                multiline
                                                minRows={3}
                                                maxRows={6}
                                                fullWidth
                                                value={line.line || ''}
                                                onChange={(e) => handleLineTextChange(idx, li, e.target.value)}
                                                placeholder="Edit line"
                                              />
                                              {/* Auto-saves as you type; no buttons needed */}
                                            </Box>
                                          )}

                                          <IconButton aria-label="delete line" color="error" onClick={() => handleDeleteLine(idx, li)}>
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddLine(idx, ch!)}>
                                      Add line
                                    </Button>
                                  </Box>
                                </>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {dropProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, chapterIndex: null })}
        aria-labelledby="delete-chapter-dialog-title"
        aria-describedby="delete-chapter-dialog-description"
      >
        <DialogTitle id="delete-chapter-dialog-title">Delete Chapter</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-chapter-dialog-description">
            Are you sure you want to delete this chapter?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmation({ open: false, chapterIndex: null })} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteChapter} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChaptersPanel;
